import { useState, useEffect } from "react";
import { useNavigate, useParams, NavigateFunction } from "react-router-dom";

import { getBookingDetailsByMasterId } from "../services/booking";
import { getCookie, formatDurationHM, extractDatePart } from "../core/utils";

export const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
export const isAndroid = /Android/.test(navigator.userAgent);
export const isMobile = isIOS || isAndroid;

export interface StatusStyle {
  bg: string;
  color: string;
  dot: string;
}

// Maps a booking/payment status string to a colour scheme for the pill.
const statusStyles = (status: string): StatusStyle => {
  const s = (status || "").toLowerCase();
  if (
    s.includes("confirm") ||
    s.includes("complete") ||
    s.includes("success") ||
    s.includes("avail") ||
    s === "paid"
  )
    return { bg: "#EAF7E9", color: "#3C9A45", dot: "#8BD44D" };
  if (
    s.includes("cancel") ||
    s.includes("dispute") ||
    s.includes("declin") ||
    s.includes("refund") ||
    s.includes("fail")
  )
    return { bg: "#FDECEC", color: "#D64545", dot: "#E86363" };
  if (s.includes("pend") || s.includes("partly") || s.includes("part"))
    return { bg: "#FFF3E5", color: "#D9822B", dot: "#FF8E3C" };
  return { bg: "#DBF1F2", color: "#267684", dot: "#267684" };
};

// "pay_at_venue" -> "Pay at Venue", etc.
const paymentMethodLabel = (method: string): string => {
  switch ((method || "").toLowerCase()) {
    case "pay_online":
      return "Paid Online";
    case "pay_at_venue":
      return "Pay at Venue (50%)";
    case "pay_at_venue_25":
      return "Pay at Venue (25%)";
    default:
      return method
        ? method
            .split("_")
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(" ")
        : "N/A";
  }
};

const fullName = (person: any, fallback: string): string =>
  [person?.first_name, person?.last_name].filter(Boolean).join(" ") || fallback;

export interface BookingServiceItem {
  id: string;
  name: string;
  stylistName: string;
  stylistEmail?: string;
  dayNum: number | "";
  monthShort: string;
  dateLabel: string;
  timeRange: string;
  duration?: number;
  price?: number;
  status: string;
  statusStyle: StatusStyle;
  paymentStatus?: string;
  availed?: string;
}

export interface UseBookingDetails {
  navigate: NavigateFunction;
  jwt: string | null;
  loading: boolean;
  error: string;
  deepLink: string;
  // master
  bookingRef: string;
  dateOfBooking: string;
  status: string;
  statusStyle: StatusStyle;
  paymentStatus: string;
  paymentStatusStyle: StatusStyle;
  paymentMethod: string;
  serviceTypeLabel: string;
  customerName: string;
  customerEmail?: string;
  // appointment summary (earliest service slot)
  dayNum: number | "";
  monthShort: string;
  timeRange: string;
  // services
  services: BookingServiceItem[];
  // payment
  gross?: number;
  discount?: number;
  total?: number;
  paid?: number;
  due?: number;
}

export const useBookingDetails = (): UseBookingDetails => {
  const { id } = useParams(); // booking master id
  const navigate = useNavigate();

  const jwt = getCookie("book-d booking token");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [booking, setBooking] = useState<any>(null);

  // The mobile app's booking-details screen is keyed by the booking_details_id
  // (per service), NOT the booking master id in the URL. Derive it from the
  // loaded booking; fall back to the master id until the data arrives.
  const bookingDetailsId =
    booking?.bookings?.find((b: any) => b?.booking_detail_id)
      ?.booking_detail_id || "";
  const deepLink = `bookd://booking-details/${bookingDetailsId || id}`;

  // iOS Smart App Banner — shows a native prompt in Safari, no JS redirect
  useEffect(() => {
    if (!isIOS || !deepLink) return;
    const meta = document.createElement("meta");
    meta.name = "apple-itunes-app";
    meta.content = `app-id=6558535790, app-argument=${deepLink}`;
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, [deepLink]);

  // Protected route — only logged-in users can view a booking
  useEffect(() => {
    if (!jwt) {
      navigate("/login", {
        state: { returnTo: `/booking-details/${id}` },
        replace: true,
      });
    }
  }, [jwt, id, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      if (!id || !jwt) return;

      setLoading(true);
      setError("");

      try {
        const res = await getBookingDetailsByMasterId(id);
        const data = res?.data;

        if (res?.status === 200 && data) {
          setBooking(data);
        } else {
          setError(
            (typeof res === "string" && res) ||
              res?.message ||
              "We couldn't find this booking."
          );
        }
      } catch {
        setError("Something went wrong. Please try again.");
      }

      setLoading(false);
    };

    fetchData();
  }, [id, jwt]);

  // ---- Derived / mapped fields ----
  const rawServices: any[] = Array.isArray(booking?.bookings)
    ? booking.bookings
    : [];

  const services: BookingServiceItem[] = rawServices.map((s: any) => {
    const start = s?.slot_start_time;
    const end = s?.slot_end_time;
    const status = s?.booking_status || booking?.bookingStatus || "Confirmed";

    return {
      id: s?.booking_detail_id || s?._id || "",
      name: s?.service?.serviceName || "Service",
      stylistName: fullName(s?.team_member, "Stylist"),
      stylistEmail: s?.team_member?.email,
      dayNum: start ? new Date(start).getDate() : "",
      monthShort: start ? extractDatePart(start, "month") : "",
      dateLabel: start ? extractDatePart(start, "date") : "",
      timeRange:
        start && end
          ? `${extractDatePart(start, "time")} - ${extractDatePart(
              end,
              "time"
            )}`
          : start
          ? extractDatePart(start, "time")
          : "N/A",
      duration: s?.serviceDuration != null ? s.serviceDuration : undefined,
      price: s?.servicePrice ?? s?.net_price,
      status,
      statusStyle: statusStyles(status),
      paymentStatus: s?.payment_status,
      availed: s?.isServiceAvailedByCustomer,
    };
  });

  // Appointment summary uses the earliest service slot.
  const earliest = rawServices
    .map((s) => s?.slot_start_time)
    .filter(Boolean)
    .sort()[0];

  const earliestService = services.find((s) => s.dayNum !== "");

  const status = booking?.bookingStatus || "Confirmed";
  const paymentStatus = booking?.paymentStatus || "";

  const serviceType = (booking?.serviceType || "").toLowerCase();
  const serviceTypeLabel = serviceType === "mobile" ? "Mobile" : "Home Studio";

  return {
    navigate,
    jwt,
    loading,
    error,
    deepLink,
    bookingRef: booking?.booking_id || "",
    dateOfBooking: booking?.date_of_booking
      ? extractDatePart(booking.date_of_booking, "date")
      : "",
    status,
    statusStyle: statusStyles(status),
    paymentStatus,
    paymentStatusStyle: statusStyles(paymentStatus),
    paymentMethod: paymentMethodLabel(booking?.paymentMethod),
    serviceTypeLabel,
    customerName: fullName(booking?.customer, "Customer"),
    customerEmail: booking?.customer?.email,
    dayNum: earliest ? new Date(earliest).getDate() : "",
    monthShort: earliest ? extractDatePart(earliest, "month") : "",
    timeRange: earliestService?.timeRange || "N/A",
    services,
    gross: booking?.gross_amount,
    discount: booking?.discount_amount,
    total: booking?.net_total,
    paid: booking?.paid_amount,
    due: booking?.due_amount,
  };
};

export { formatDurationHM };
